import React, { useState } from "react";
import FormLayout from "../form-layout";
import RejectionModal from "./rejection-modal";
import { Q12_ALLERGY_MEDICATIONS } from "../../data/eligibility-rules";
import { checkImmediateRed } from "../../lib/eligibility";

// Q12 — "Are you allergic to any of these medications?" A plain "Yes" is the reject
// trigger per the doc — there's no "which one(s)" follow-up.
export default function Step10({ formData, setFormData, onBack, goToStep }) {
  const [answer, setAnswer] = useState(formData.allergyAnswer || "");
  const [otherAllergiesText, setOtherAllergiesText] = useState(formData.otherAllergiesText || "");
  const [rejection, setRejection] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!answer) return;

    const nextFormData = {
      ...formData,
      allergyAnswer: answer,
      hasOtherAllergies: answer === "other",
      otherAllergiesText: answer === "other" ? otherAllergiesText : "",
    };
    setFormData(nextFormData);
    localStorage.setItem("allergyAnswer", answer);
    localStorage.setItem("otherAllergiesText", nextFormData.otherAllergiesText);

    const redFlag = checkImmediateRed(nextFormData);
    if (redFlag) {
      setRejection(redFlag);
      return;
    }

    // Form B ("currently using") patients already gave us their medication/dose/last
    // dose date at the start of the form, so the Q13-18 "have you ever taken GLP-1
    // before" follow-up pathway is redundant for them — skip straight to the
    // pregnancy gate (female) / additional info (male), same as where that pathway
    // itself exits. Form A ("naive") still goes through it as the doc specifies.
    if (formData.glp1History === "current") {
      goToStep(formData.biologicalSex === "Female" ? "pregnancyGate" : "additionalInfo");
    } else {
      goToStep("13");
    }
  };

  return (
    <FormLayout title="" goToStep={goToStep} onBack={onBack}>
      <h2 className="text-sm sm:text-[14px] font-medium text-[#000000] mb-2 text-center">
        Are you allergic to any of these medications?
      </h2>

      <ul className="flex flex-col gap-2 mb-4 list-disc list-inside text-sm text-gray-700">
        {Q12_ALLERGY_MEDICATIONS.map((med) => (
          <li key={med.key}>{med.label}</li>
        ))}
      </ul>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          {["yes", "no", "other"].map((option) => (
            <label key={option} className="flex items-center gap-2 p-3 rounded-lg cursor-pointer border transition-all text-sm">
              <input
                type="radio"
                name="allergyAnswer"
                checked={answer === option}
                onChange={() => setAnswer(option)}
                className="h-4 w-4 accent-sky-400 cursor-pointer"
              />
              <span>
                {option === "yes" && "Yes"}
                {option === "no" && "No"}
                {option === "other" && "No, but I have other allergies"}
              </span>
            </label>
          ))}
        </div>

        {answer === "other" && (
          <textarea
            value={otherAllergiesText}
            onChange={(e) => setOtherAllergiesText(e.target.value)}
            placeholder="Please describe your other allergies."
            className="border border-gray-300 rounded-lg p-3 text-sm w-full min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#35C3D9]"
          />
        )}

        <div className="flex justify-start">
          <button
            type="submit"
            disabled={!answer}
            className={`w-[210px] text-sm font-semibold rounded-lg py-3 transition-colors
              ${!answer ? "bg-gray-300 text-gray-600 cursor-not-allowed" : "bg-[#35C3D9] text-white hover:bg-[#27AAC0]"}`}
          >
            Next →
          </button>
        </div>
      </form>

      {rejection && <RejectionModal onClose={() => setRejection(null)} />}
    </FormLayout>
  );
}
